import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Reveal } from "../components/Reveal";
import { ArrowRight, LogOut, Settings } from "lucide-react";

export default function Account() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [courses, setCourses] = useState(null);

  useEffect(() => { document.title = "My Library · Il Salotto Digitale"; }, []);
  useEffect(() => { api.get("/my/courses").then((r) => setCourses(r.data)).catch(() => setCourses([])); }, []);

  return (
    <div data-testid="account-page" className="min-h-screen pt-20">
      <section className="mx-auto max-w-[1600px] px-6 lg:px-12 pt-16 lg:pt-24 pb-10">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 border-b border-line pb-10">
          <div>
            <p className="text-[11px] uppercase tracking-ultra text-accent">My Library</p>
            <h1 className="mt-4 font-serif font-light text-4xl lg:text-6xl text-ink tracking-tight">
              Welcome, {user?.name?.split(" ")[0] || "there"}
            </h1>
          </div>
          <div className="flex gap-3">
            {user?.role === "admin" && (
              <Link to="/admin" data-testid="account-admin-link" className="border border-ink text-ink px-6 py-3 text-[11px] uppercase tracking-[0.2em] hover:bg-ink hover:text-bg transition-colors flex items-center gap-2">
                <Settings size={14} /> Admin CMS
              </Link>
            )}
            <button onClick={() => { logout(); nav("/"); }} data-testid="logout-btn"
              className="border border-line text-ink-soft px-6 py-3 text-[11px] uppercase tracking-[0.2em] hover:border-ink hover:text-ink transition-colors flex items-center gap-2">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1600px] px-6 lg:px-12 pb-32">
        <p className="text-[11px] uppercase tracking-ultra text-ink-soft mb-10">Your Courses</p>
        {courses === null ? (
          <p className="text-sm text-ink-soft animate-pulse">Loading your library…</p>
        ) : courses.length === 0 ? (
          <div className="border border-line p-12 text-center">
            <h2 className="font-serif text-3xl text-ink">Your library is empty</h2>
            <p className="mt-3 text-sm text-ink-soft">Explore Il Salotto Digitale and enrol in your first course.</p>
            <Link to="/salotto-digitale" className="mt-8 inline-flex items-center gap-2 bg-ink text-bg px-8 py-4 text-[11px] uppercase tracking-[0.2em]">
              Browse courses <ArrowRight size={14} />
            </Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 border-t border-l border-line">
            {courses.map((c, i) => {
              const isP2 = c.id === "becoming-an-estate-manager";
              const isTool = c.id === "residence-service-record";
              const to = isP2 ? "/becoming" : isTool ? "/tool/residence-service-record" : `/learn/${c.id}`;
              return (
              <Reveal key={c.id} delay={i * 0.06} className="border-b border-r border-line">
                <Link to={to} data-testid={`account-course-${c.id}`} className="group block h-full bg-bg">
                  <div className="overflow-hidden aspect-[16/10]">
                    {isTool ? <img src="/rsr-card.webp" alt={c.title} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" />
                      : c.cover_url ? <img src={c.cover_url} alt={c.title} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" />
                      : <div className="w-full h-full bg-surface" />}
                  </div>
                  <div className="p-8">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-accent mb-2">{c.tag}</p>
                    <h3 className="font-serif text-2xl text-ink leading-snug">{c.title}</h3>
                    {isP2 ? (
                      <p className="mt-6 text-sm font-light text-ink-soft">A guided two-path course. Choose your path inside.</p>
                    ) : isTool ? (
                      <p className="mt-6 text-sm font-light text-ink-soft">Your professional tool — download the workbook and Quick Start Guide.</p>
                    ) : (
                    <div className="mt-6">
                      <div className="flex justify-between text-[11px] uppercase tracking-[0.15em] text-ink-soft mb-2">
                        <span>Progress</span><span>{c.progress_pct}%</span>
                      </div>
                      <div className="h-px bg-line relative">
                        <div className="absolute left-0 top-0 h-px bg-ink transition-all" style={{ width: `${c.progress_pct}%` }} />
                      </div>
                    </div>
                    )}
                    <span className="mt-6 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-ink">
                      {isP2 ? "Enter the course" : isTool ? "Open the tool" : (c.progress_pct > 0 ? "Continue" : "Start")} <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              </Reveal>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
